import type { DenseHeaderMetricModel } from '../../presentation/cards/types';
import type {
  ExecutionActivityViewModel,
  ExecutionTripMetrics,
  ExecutionTripSort,
  ExecutionTripViewModel,
} from './executionTypes';

export const isTerminalDelivery = (activity: ExecutionActivityViewModel): boolean => (
  activity.kind === 'TOUCH_POINT' && (activity.outcome === 'SUCCESS' || activity.outcome === 'RTO')
);

export const isExecutionException = (activity: ExecutionActivityViewModel): boolean => (
  activity.exceptionTags.length > 0 || activity.outcome === 'RTO' || activity.kind === 'UNPLANNED_STOP'
);

export const orderActivitiesForDetails = (
  activities: ExecutionActivityViewModel[]
): ExecutionActivityViewModel[] => {
  const departure = activities.filter((activity) => activity.kind === 'DEPOT_DEPARTURE');
  const actual = activities
    .filter((activity) => activity.kind !== 'DEPOT_DEPARTURE' && activity.actualSequence != null)
    .sort((left, right) => (left.actualSequence ?? 0) - (right.actualSequence ?? 0));
  const remaining = activities
    .filter((activity) => (
      activity.kind !== 'DEPOT_DEPARTURE' &&
      activity.kind !== 'DEPOT_RETURN' &&
      activity.actualSequence == null
    ))
    .sort((left, right) => (left.plannedSequence ?? Number.MAX_SAFE_INTEGER) - (right.plannedSequence ?? Number.MAX_SAFE_INTEGER));
  const pendingReturn = activities.filter((activity) => (
    activity.kind === 'DEPOT_RETURN' && activity.actualSequence == null
  ));

  return [...departure, ...actual, ...remaining, ...pendingReturn];
};

export const buildExecutionMetrics = (
  activities: ExecutionActivityViewModel[],
  plannedDistanceKm: number,
  plannedDurationMinutes: number,
  actualDurationMinutes: number
): ExecutionTripMetrics => {
  const deliveries = activities.filter((activity) => activity.kind === 'TOUCH_POINT');
  const terminal = deliveries.filter(isTerminalDelivery);
  const successCount = terminal.filter((activity) => activity.outcome === 'SUCCESS').length;
  const rtoCount = terminal.filter((activity) => activity.outcome === 'RTO').length;
  const completedTravel = activities.filter((activity) => activity.incomingTravel.actualDistanceKm != null);
  const actualDistanceKm = completedTravel.reduce(
    (sum, activity) => sum + (activity.incomingTravel.actualDistanceKm ?? 0),
    0
  );
  const plannedComparableDistanceKm = completedTravel.reduce(
    (sum, activity) => sum + (activity.incomingTravel.plannedDistanceKm ?? 0),
    0
  );
  const progressPercent = deliveries.length ? (terminal.length / deliveries.length) * 100 : 0;
  const plannedComparableDurationMinutes = plannedDistanceKm > 0
    ? plannedDurationMinutes * Math.min(1, plannedComparableDistanceKm / plannedDistanceKm)
    : 0;
  const latestDelta = Math.max(
    0,
    ...activities.map((activity) => activity.arrivalDeltaMinutes ?? 0)
  );

  return {
    plannedDistanceKm,
    plannedComparableDistanceKm,
    actualDistanceKm,
    plannedDurationMinutes,
    plannedComparableDurationMinutes,
    actualDurationMinutes,
    progressPercent,
    terminalCount: terminal.length,
    totalDeliveryCount: deliveries.length,
    successCount,
    rtoCount,
    successPercent: terminal.length ? (successCount / terminal.length) * 100 : 0,
    delayMinutes: latestDelta,
  };
};

const attentionScore = (trip: ExecutionTripViewModel): number => (
  trip.attentionReasons.length * 1000 +
  trip.metrics.rtoCount * 200 +
  trip.metrics.delayMinutes * 2 +
  (trip.phase === 'DELAYED' ? 100 : 0)
);

export const sortExecutionTrips = (
  trips: ExecutionTripViewModel[],
  sort: ExecutionTripSort
): ExecutionTripViewModel[] => [...trips].sort((left, right) => {
  if (sort === 'attention') return attentionScore(right) - attentionScore(left) || left.id.localeCompare(right.id);
  if (sort === 'progress') return right.metrics.progressPercent - left.metrics.progressPercent || left.id.localeCompare(right.id);
  if (sort === 'delay') return right.metrics.delayMinutes - left.metrics.delayMinutes || left.id.localeCompare(right.id);
  if (sort === 'distance') return right.metrics.actualDistanceKm - left.metrics.actualDistanceKm || left.id.localeCompare(right.id);
  return left.id.localeCompare(right.id);
});

const formatHours = (minutes: number): string => `${(minutes / 60).toFixed(1)}h`;

const formatSigned = (value: number, suffix: string): string => (
  `${value >= 0 ? '+' : ''}${value.toFixed(1)}${suffix}`
);

export const buildExecutionDayMetrics = (trips: ExecutionTripViewModel[]): DenseHeaderMetricModel[] => {
  const totals = trips.reduce((acc, trip) => ({
    plannedKm: acc.plannedKm + trip.metrics.plannedComparableDistanceKm,
    actualKm: acc.actualKm + trip.metrics.actualDistanceKm,
    plannedMinutes: acc.plannedMinutes + trip.metrics.plannedComparableDurationMinutes,
    actualMinutes: acc.actualMinutes + trip.metrics.actualDurationMinutes,
    terminal: acc.terminal + trip.metrics.terminalCount,
    deliveries: acc.deliveries + trip.metrics.totalDeliveryCount,
    success: acc.success + trip.metrics.successCount,
    rto: acc.rto + trip.metrics.rtoCount,
  }), { plannedKm: 0, actualKm: 0, plannedMinutes: 0, actualMinutes: 0, terminal: 0, deliveries: 0, success: 0, rto: 0 });
  const outcomeCount = totals.success + totals.rto;
  const distanceDelta = totals.actualKm - totals.plannedKm;
  const durationDeltaMinutes = totals.actualMinutes - totals.plannedMinutes;

  return [
    { key: 'trips', label: 'Trips', value: String(trips.length) },
    {
      key: 'distance',
      label: 'Distance',
      value: `${totals.actualKm.toFixed(1)} km`,
      deltaText: formatSigned(distanceDelta, ' km'),
      deltaValue: distanceDelta,
      deltaDirection: 'down-good',
      tooltipTitle: 'Actual distance',
      tooltipDescription: `Compared with ${totals.plannedKm.toFixed(1)} km planned for completed travel.`,
    },
    {
      key: 'duration',
      label: 'Duration',
      value: formatHours(totals.actualMinutes),
      deltaText: formatSigned(durationDeltaMinutes / 60, 'h'),
      deltaValue: durationDeltaMinutes,
      deltaDirection: 'down-good',
      tooltipTitle: 'Actual duration',
      tooltipDescription: `Compared with ${formatHours(totals.plannedMinutes)} planned for completed travel.`,
    },
    { key: 'success', label: 'Success', value: `${outcomeCount ? Math.round((totals.success / outcomeCount) * 100) : 0}%` },
    { key: 'rto', label: 'RTO', value: String(totals.rto) },
    { key: 'progress', label: 'Delivery progress', value: `${totals.deliveries ? Math.round((totals.terminal / totals.deliveries) * 100) : 0}%` },
  ];
};
